import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, Navigation } from "lucide-react";

const PREF_KEY = "pacemia_driver_nav_app";
const readPref = () => localStorage.getItem(PREF_KEY) || "";
const writePref = (v) => v ? localStorage.setItem(PREF_KEY, v) : localStorage.removeItem(PREF_KEY);

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// Génère les URL scheme pour chaque application de navigation.
// Chaque app est ouverte via un deep link natif — pas de coût côté PACEMIA.
export const navUrls = (addr, lat, lon) => {
  const dest = (lat && lon) ? `${lat},${lon}` : encodeURIComponent(addr || "");
  const q = (lat && lon) ? `${lat},${lon}` : encodeURIComponent(addr || "");
  return {
    // Universal Google Maps link — ouvre l'app native Android/iOS si installée, sinon le web.
    google: `https://www.google.com/maps/dir/?api=1&destination=${dest}`,
    // Waze : deep link universel — ouvre l'app si installée, sinon proposition d'installation.
    waze: (lat && lon)
      ? `https://waze.com/ul?ll=${lat}%2C${lon}&navigate=yes`
      : `https://waze.com/ul?q=${q}&navigate=yes`,
    // Apple Plans : URL scheme officiel Apple, natif iOS/macOS.
    apple: `http://maps.apple.com/?daddr=${dest}`,
  };
};

const APPS = [
  { id: "google", label: "Google Maps", desc: "Recommandé — trafic temps réel", color: "bg-blue-500", initial: "G" },
  { id: "waze",   label: "Waze",        desc: "Communauté & alertes radars", color: "bg-cyan-500", initial: "W" },
  { id: "apple",  label: "Apple Plans", desc: "iPhone / iPad natif",         color: "bg-slate-600", initial: "A" },
];

// Ouvre un modal de choix (si aucune préférence) ou lance directement l'app préférée.
// Un lien Coyote / iOS-Plans peut coexister (Coyote n'a pas d'URL publique standard, on redirige vers l'App Store si besoin).
export function NavAppPicker({ open, onClose, address, lat, lon, testid = "nav-picker" }) {
  const [remember, setRemember] = useState(true);
  const urls = navUrls(address, lat, lon);
  const launch = (app) => {
    if (remember) writePref(app);
    window.open(urls[app], "_blank", "noopener,noreferrer");
    onClose?.();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Navigation className="h-5 w-5 text-primary" /> Naviguer avec…</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground">
          Choisissez votre application de navigation habituelle. Elle s'ouvrira avec l'itinéraire déjà chargé.
        </DialogDescription>
        <div className="space-y-2 mt-2">
          {APPS.filter(a => a.id !== "apple" || isIOS()).map((a) => (
            <button key={a.id} type="button" onClick={() => launch(a.id)}
              className="w-full flex items-center gap-3 rounded-md border border-border p-3 hover:bg-secondary active:opacity-70 transition"
              data-testid={`${testid}-${a.id}`}>
              <div className={`h-9 w-9 rounded-md ${a.color} text-white font-bold flex items-center justify-center`}>{a.initial}</div>
              <div className="flex-1 text-left min-w-0">
                <div className="font-medium truncate">{a.label}</div>
                <div className="text-[11px] text-muted-foreground truncate">{a.desc}</div>
              </div>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer mt-2" data-testid={`${testid}-remember-label`}>
          <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid={`${testid}-remember`} />
          Mémoriser mon choix pour les prochaines fois
        </label>
        <div className="text-[11px] text-muted-foreground pt-1">
          Toutes les apps ci-dessus utilisent leur propre GPS/moteur — PACEMIA ne facture aucun trajet.
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Ouvre directement l'app préférée si elle est mémorisée ; sinon appelle openPicker().
export function launchOrPick({ address, lat, lon, openPicker }) {
  const pref = readPref();
  if (pref) {
    const urls = navUrls(address, lat, lon);
    if (urls[pref]) {
      window.open(urls[pref], "_blank", "noopener,noreferrer");
      return;
    }
  }
  openPicker?.();
}

export function clearNavPref() { writePref(""); }
export function getNavPref() { return readPref(); }
